/**
 * src/pages/ForgotPassword.jsx — asks for an email and sends a reset link.
 *
 * Public, no session, same plain card as Login.jsx (an account page, not a
 * studio portal page). After submitting it always shows the same message,
 * whatever the API found: the API answers identically for known and unknown
 * emails (see api/src/routes/auth.js), and this page must not undo that by
 * saying anything different either.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useNoindex } from '../lib/useNoindex.js';
import { ErrorBanner } from '../components/ui.jsx';

export default function ForgotPassword() {
  useNoindex();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      // Only reachable for a real failure (network, 429) — never "no such
      // account", which the API doesn't report.
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="public-page" style={{ maxWidth: 420 }}>
      <div className="public-header">
        <img src="/favicon.svg" alt="" width="40" height="40" />
        <h1>Reset your password</h1>
        <p className="muted small">We&rsquo;ll email you a link to choose a new one.</p>
      </div>

      {sent ? (
        <div className="card stack">
          <div className="alert alert-success" role="status">
            If that email has an account, we&rsquo;ve sent a link.
          </div>
          <p className="muted small">The link works once and expires in 1 hour.</p>
          <Link className="btn" to="/login">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form className="card stack" onSubmit={submit}>
          <ErrorBanner>{error}</ErrorBanner>
          <div>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
          <Link className="btn" to="/login">
            Back to sign in
          </Link>
        </form>
      )}
    </div>
  );
}
